import type { ParsedCoordinate, CurveEndPoint, SVGPathData, CoordinatePoint } from '@/types'

/**
 * Parse a coordinate string into structured data
 * Examples: "n43.15e 351.85", "curve left radius 305.74 delta 46.45"
 */
export function parseCoordinate(coord: string): ParsedCoordinate {
  const parts = coord.toLowerCase().split(' ')
  
  // Check for chord-based curve format: "c l r2009.86 n06.26.00w 386.17f" or "c r491.00 s13.44.41w 224.47f"
  if (parts.length >= 4 && parts[0] === 'c') {
    let direction = 'right' // Default direction
    let radiusStr = ''
    let startIndex = 1
    
    // Check if second part is direction (l/r) or radius (r###)
    if (parts[1] === 'l' || parts[1] === 'r') {
      // Format: "c l r2009.86 n06.26.00w 386.17f"
      direction = parts[1] === 'l' ? 'left' : 'right'
      radiusStr = parts[2].replace('r', '')
      startIndex = 3
    } else if (parts[1].startsWith('r')) {
      // Format: "c r491.00 s13.44.41w 224.47f"
      radiusStr = parts[1].replace('r', '')
      startIndex = 2
    }
    
    const radius = parseFloat(radiusStr)
    
    // Parse chord bearing and length
    let chordBearing = ''
    let chordLength = 0
    
    // Find bearing (format like "n06.26.00w" or "s13.44.41w") and length (format like "386.17f")
    for (let i = startIndex; i < parts.length; i++) {
      const part = parts[i]
      if (/^[nsew]\d+\.\d+\.\d+[nsew]$/i.test(part)) {
        chordBearing = part
      } else if (/^\d+\.\d+f?$/i.test(part)) {
        chordLength = parseFloat(part.replace('f', ''))
      }
    }
    
    // Calculate delta angle from chord length and radius
    // chord = 2 * radius * sin(delta/2)
    // delta = 2 * arcsin(chord / (2 * radius))
    let delta = 0
    if (radius > 0 && chordLength > 0) {
      const sinHalfDelta = chordLength / (2 * radius)
      if (sinHalfDelta <= 1) { // Valid sine value
        delta = 2 * Math.asin(sinHalfDelta) * (180 / Math.PI) // Convert to degrees
      }
    }
    
    return { 
      type: 'curve', 
      direction, 
      radius, 
      delta,
      chordBearing,
      chordLength
    }
  }
  // Check for enhanced words-based chord curve format: "curve left chord radius 2009.86 bearing n06.26.00w length 386.17"
  else if (parts.includes('curve') && parts.includes('chord') && parts.includes('bearing') && parts.includes('length')) {
    const direction = parts[1] // left or right
    const radiusIndex = parts.indexOf('radius')
    const bearingIndex = parts.indexOf('bearing')
    const lengthIndex = parts.indexOf('length')
    
    const radius = radiusIndex !== -1 ? parseFloat(parts[radiusIndex + 1]) : 0
    const chordBearing = bearingIndex !== -1 ? parts[bearingIndex + 1] : ''
    const chordLength = lengthIndex !== -1 ? parseFloat(parts[lengthIndex + 1]) : 0
    
    // Calculate delta angle from chord length and radius
    let delta = 0
    if (radius > 0 && chordLength > 0) {
      const sinHalfDelta = chordLength / (2 * radius)
      if (sinHalfDelta <= 1) { // Valid sine value
        delta = 2 * Math.asin(sinHalfDelta) * (180 / Math.PI) // Convert to degrees
      }
    }
    
    return { 
      type: 'curve', 
      direction, 
      radius, 
      delta,
      chordBearing,
      chordLength
    }
  }
  // Check for standard curve format: "curve left radius 305.74 delta 46.45"
  else if (parts.includes('curve')) {
    const radiusIndex = parts.indexOf('radius')
    const deltaIndex = parts.indexOf('delta')
    const direction = parts[1] // left or right
    const radius = radiusIndex !== -1 ? parseFloat(parts[radiusIndex + 1]) : 0
    
    let delta = 0
    if (deltaIndex !== -1) {
      const deltaStr = parts[deltaIndex + 1]
      
      // Handle different delta formats (same logic as line coordinates)
      if (deltaStr.includes('.')) {
        const deltaParts = deltaStr.split('.')
        if (deltaParts.length === 3 && deltaParts[1].length <= 2 && deltaParts[2].length <= 2) {
          // Degrees.minutes.seconds format (46.45.00 = 46° 45' 0")
          delta = parseFloat(deltaParts[0]) + parseFloat(deltaParts[1]) / 60 + parseFloat(deltaParts[2]) / 3600
        } else if (deltaParts.length === 2 && deltaParts[1].length <= 2 && parseFloat(deltaParts[1]) < 60) {
          // Degrees.minutes format (46.45 = 46° 45')
          delta = parseFloat(deltaParts[0]) + parseFloat(deltaParts[1]) / 60
        } else {
          // Decimal degrees format (46.75 = 46.75°)
          delta = parseFloat(deltaStr)
        }
      } else {
        delta = parseFloat(deltaStr)
      }
    }
    
    return { 
      type: 'curve', 
      direction, 
      radius, 
      delta 
    }
  } else {
    // Parse line: "n43.15e 351.85" or "N43.15E 351.85"
    const directionPart = parts[0]
    const distance = parseFloat(parts[1])
    
    // Extract direction (first and last character: n43.15e -> "ne")
    const direction = directionPart[0] + directionPart.slice(-1)
    
    // Extract degrees (middle part: n43.15e -> "43.15")
    const degreesStr = directionPart.slice(1, -1)
    let degrees = 0
    
    // Handle different degree formats
    if (degreesStr.includes('.')) {
      const parts = degreesStr.split('.')
      if (parts.length === 3 && parts[1].length <= 2 && parts[2].length <= 2) {
        // Degrees.minutes.seconds format (06.26.00 = 6° 26' 0")
        degrees = parseFloat(parts[0]) + parseFloat(parts[1]) / 60 + parseFloat(parts[2]) / 3600
      } else if (parts.length === 2 && parts[1].length <= 2 && parseFloat(parts[1]) < 60) {
        // Degrees.minutes format (21.22 = 21° 22')
        degrees = parseFloat(parts[0]) + parseFloat(parts[1]) / 60
      } else {
        // Decimal degrees format (21.2228 = 21.2228°)
        degrees = parseFloat(degreesStr)
      }
    } else {
      degrees = parseFloat(degreesStr)
    }
    
    return { 
      type: 'line', 
      direction, 
      degrees, 
      distance 
    }
  }
}

/**
 * Convert surveying bearing to end coordinates
 * Surveying bearings: N/S indicates reference direction, angle shows deviation, E/W shows final direction
 */
export function getCoordinatesFromDirection(
  direction: string, 
  angle: number, 
  distance: number, 
  startX: number, 
  startY: number
): CoordinatePoint {
  // Convert surveying bearing to true azimuth (0° = North, clockwise)
  let azimuth = 0
  
  const firstQuadrant = direction[0].toLowerCase() // n or s
  const secondQuadrant = direction[1].toLowerCase() // e or w
  
  if (firstQuadrant === 'n' && secondQuadrant === 'e') {
    // NE quadrant: 0° to 90°
    azimuth = angle
  } else if (firstQuadrant === 's' && secondQuadrant === 'e') {
    // SE quadrant: 90° to 180°
    azimuth = 180 - angle
  } else if (firstQuadrant === 's' && secondQuadrant === 'w') {
    // SW quadrant: 180° to 270°
    azimuth = 180 + angle
  } else if (firstQuadrant === 'n' && secondQuadrant === 'w') {
    // NW quadrant: 270° to 360°
    azimuth = 360 - angle
  }
  
  // Convert to radians
  const azimuthRad = (azimuth * Math.PI) / 180
  
  // Calculate coordinates (surveying: Y is North, X is East)
  const deltaX = distance * Math.sin(azimuthRad) // East component
  const deltaY = distance * Math.cos(azimuthRad) // North component
  
  return {
    x: startX + deltaX,
    y: startY + deltaY
  }
}

/**
 * Convert surveying bearing to true azimuth (0° = North, clockwise)
 */
export function calculateBearingFromDirection(direction: string, degrees: number): number {
  const firstQuadrant = direction[0].toLowerCase() // n or s
  const secondQuadrant = direction[1].toLowerCase() // e or w
  
  if (firstQuadrant === 'n' && secondQuadrant === 'e') {
    return degrees // NE quadrant: 0° to 90°
  } else if (firstQuadrant === 's' && secondQuadrant === 'e') {
    return 180 - degrees // SE quadrant: 90° to 180°
  } else if (firstQuadrant === 's' && secondQuadrant === 'w') {
    return 180 + degrees // SW quadrant: 180° to 270°
  } else if (firstQuadrant === 'n' && secondQuadrant === 'w') {
    return 360 - degrees // NW quadrant: 270° to 360°
  }
  return 0
}

/**
 * Calculate the end point of a curve
 */
export function getCurveEndPoint(
  startX: number,
  startY: number,
  radius: number,
  delta: number,
  initialBearing: number,
  direction: string
): CurveEndPoint {
  const endBearing = direction === 'left' ? initialBearing + delta : initialBearing - delta
  const endBearingRad = (endBearing * Math.PI) / 180
  const initialBearingRad = (initialBearing * Math.PI) / 180

  // Calculate curve center
  const centerX = direction === 'left'
    ? startX - radius * Math.sin(initialBearingRad)
    : startX + radius * Math.sin(initialBearingRad)
    
  const centerY = direction === 'left'
    ? startY + radius * Math.cos(initialBearingRad)
    : startY - radius * Math.cos(initialBearingRad)

  // Calculate end point
  const endX = centerX + radius * Math.sin(endBearingRad)
  const endY = centerY - radius * Math.cos(endBearingRad)

  return {
    endX,
    endY,
    centerX,
    centerY,
    initialBearing,
    endBearing
  }
}

/**
 * Generate SVG path data from coordinate strings
 */
export function generateSvgPath(coords: string[]): SVGPathData {
  const svgPaths: string[] = []
  let startX = 0
  let startY = 0
  let currentBearing = 0
  let maxX = 0
  let maxY = 0
  let minX = 0
  let minY = 0

  for (const coord of coords) {
    const parsed = parseCoordinate(coord)
    
    if (parsed.type === 'line' && parsed.direction && parsed.degrees !== undefined && parsed.distance !== undefined) {
      const endPoint = getCoordinatesFromDirection(parsed.direction, parsed.degrees, parsed.distance, startX, startY)
      
      // Create SVG path (flip Y for SVG coordinate system)
      svgPaths.push(`M ${startX} ${-startY} L ${endPoint.x} ${-endPoint.y}`)
      
      startX = endPoint.x
      startY = endPoint.y
      currentBearing = calculateBearingFromDirection(parsed.direction, parsed.degrees)

      // Update bounds
      if (startX > maxX) maxX = startX
      if (startY > maxY) maxY = startY
      if (startX < minX) minX = startX
      if (startY < minY) minY = startY
      
    } else if (parsed.type === 'curve' && parsed.direction && parsed.radius && parsed.delta) {
      let curveEnd: CurveEndPoint
      
      // For chord-based curves, calculate end point from chord bearing and length
      if (parsed.chordBearing && parsed.chordLength) {
        // Parse chord bearing to get direction and degrees
        const chordDir = parsed.chordBearing[0] + parsed.chordBearing.slice(-1)
        const chordDegreesStr = parsed.chordBearing.slice(1, -1)
        const chordParts = chordDegreesStr.split('.')
        const chordDegrees = parseFloat(chordParts[0]) + parseFloat(chordParts[1]) / 60 + parseFloat(chordParts[2]) / 3600
        
        // Calculate end point directly from chord
        const chordEndPoint = getCoordinatesFromDirection(chordDir, chordDegrees, parsed.chordLength, startX, startY)
        
        curveEnd = {
          endX: chordEndPoint.x,
          endY: chordEndPoint.y,
          centerX: 0, // Will calculate if needed
          centerY: 0, // Will calculate if needed
          initialBearing: currentBearing,
          endBearing: parsed.direction === 'left' ? currentBearing + parsed.delta : currentBearing - parsed.delta
        }
      } else {
        // Standard delta-based curve
        curveEnd = getCurveEndPoint(startX, startY, parsed.radius, parsed.delta, currentBearing, parsed.direction)
      }
      
      // Create SVG arc path (flip Y for SVG coordinate system)
      const largeArcFlag = parsed.delta > 180 ? 1 : 0
      // Use v1 sweep flag logic (which was working)
      const sweepFlag = parsed.direction === 'left' ? 0 : 1
      
      svgPaths.push(
        `M ${startX} ${-startY} A ${parsed.radius} ${parsed.radius} 0 ${largeArcFlag},${sweepFlag} ${curveEnd.endX} ${-curveEnd.endY}`
      )
      
      startX = curveEnd.endX
      startY = curveEnd.endY
      currentBearing = parsed.direction === 'left' ? currentBearing + parsed.delta : currentBearing - parsed.delta
      
      // Normalize bearing to 0-360° range
      currentBearing = ((currentBearing % 360) + 360) % 360

      // Update bounds
      if (startX > maxX) maxX = startX
      if (startY > maxY) maxY = startY
      if (startX < minX) minX = startX
      if (startY < minY) minY = startY
    }
  }

  // Calculate bounds and viewBox
  const width = maxX - minX
  const height = maxY - minY
  const buffer = Math.max(width, height) * 0.1 || 50 // 10% buffer or minimum 50 units
  
  const viewBox = `${minX - buffer} ${-maxY - buffer} ${width + 2 * buffer} ${height + 2 * buffer}`

  return {
    svgPaths,
    viewBox,
    bounds: {
      minX,
      minY,
      maxX,
      maxY,
      width,
      height
    }
  }
}

/**
 * Validate coordinate format
 */
export function validateCoordinate(coord: string): boolean {
  const trimmed = coord.trim().toLowerCase()
  
  // Check for chord-based curve format: "c l r2009.86 n06.26.00w 386.17f" or "c r491.00 s13.44.41w 224.47f"
  if (trimmed.startsWith('c ')) {
    // Format with direction: "c l r2009.86 n06.26.00w 386.17f"
    const withDirection = /^c\s+[lr]\s+r[\d.]+\s+[nsew]\d+\.\d+\.\d+[nsew]\s+[\d.]+f?\s*/i.test(trimmed)
    // Format without direction: "c r491.00 s13.44.41w 224.47f"
    const withoutDirection = /^c\s+r[\d.]+\s+[nsew]\d+\.\d+\.\d+[nsew]\s+[\d.]+f?\s*/i.test(trimmed)
    return withDirection || withoutDirection
  }
  
  // Check for enhanced words-based chord curve format: "curve left chord radius 2009.86 bearing n06.26.00w length 386.17"
  if (trimmed.includes('curve') && trimmed.includes('chord') && trimmed.includes('bearing') && trimmed.includes('length')) {
    return /curve\s+(left|right)\s+chord\s+radius\s+[\d.]+\s+bearing\s+[nsew]\d+\.\d+\.\d+[nsew]\s+length\s+[\d.]+/.test(trimmed)
  }
  
  // Check for standard curve format: "curve left radius 305.74 delta 46.45" or "curve left radius 305.74 delta 46.45.00"
  if (trimmed.includes('curve')) {
    // Updated regex to handle decimal degrees (46.45), degrees.minutes (46.45), or degrees.minutes.seconds (46.45.00)
    return /curve\s+(left|right)\s+radius\s+[\d.]+\s+delta\s+(\d+\.\d+\.\d+|\d+\.\d+|\d+)/.test(trimmed)
  }
  
  // Check for line format - handle multiple formats:
  // Standard: "n43.15e 351.85"
  // Real-world: "N21.22.28W 209.66F" or "S1.55.32W 200.25F"
  // Complex: "N6.26.0W 386.17F L 2009.86R 386.77A" (with additional surveying data)
  
  // Basic coordinate patterns
  const basicPattern1 = /^[nsew][\d.]+[nsew]\s+[\d.]+[f]?/i
  const basicPattern2 = /^[nsew]\d+\.\d+\.\d+[nsew]\s+\d+\.\d+[f]?/i
  
  // Check if it starts with a valid coordinate pattern (ignore additional surveying data)
  return basicPattern1.test(trimmed) || basicPattern2.test(trimmed)
}

/**
 * Format coordinate for display
 */
export function formatCoordinate(coord: string): string {
  const parsed = parseCoordinate(coord)
  
  if (parsed.type === 'curve') {
    if (parsed.chordBearing && parsed.chordLength) {
      // Enhanced words-based chord curve format
      return `Curve ${parsed.direction} chord radius ${parsed.radius?.toFixed(2)} bearing ${parsed.chordBearing} length ${parsed.chordLength?.toFixed(2)}`
    } else {
      // Standard curve format
      return `Curve ${parsed.direction} R=${parsed.radius?.toFixed(2)}ft Δ=${parsed.delta?.toFixed(2)}°`
    }
  } else if (parsed.type === 'line') {
    const direction = parsed.direction?.toUpperCase()
    return `${direction} ${parsed.degrees?.toFixed(2)}° ${parsed.distance?.toFixed(2)}ft`
  }
  
  return coord
} 